import React, { useState, useEffect, useMemo } from 'react';
import { 
  Grid, Paper, Typography, Box, FormControl, InputLabel, 
  Select, MenuItem, Dialog, DialogTitle, DialogContent, 
  IconButton, Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListSubheader,
  Tooltip
} from '@mui/material';
import { Bar, Line } from 'react-chartjs-2';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LoadingState from './loadingstate';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const RegionalComparisonAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareType, setCompareType] = useState('fire');
  const [selectedRegions, setSelectedRegions] = useState(['Kuching', 'Miri']);
  const [startDate, setStartDate] = useState(dayjs().subtract(5, 'year'));
  const [endDate, setEndDate] = useState(dayjs());
  const [expandedChart, setExpandedChart] = useState(null);
  
  // Set min/max dates for the date picker, only relevant for 'fire'
  const minDate = dayjs('2015-01-01');
  const maxDate = dayjs();

  const divisionGroupings = {
    'Kuching': ['Kuching', 'Bau', 'Lundu'],
    'Samarahan': ['Samarahan', 'Asajaya', 'Simunjan'],
    'Serian': ['Serian'],
    'Sri Aman': ['Sri Aman', 'Lubok Antu'],
    'Betong': ['Betong', 'Saratok'],
    'Sarikei': ['Sarikei', 'Pakan', 'Julau', 'Meradong'],
    'Sibu': ['Sibu', 'Kanowit', 'Selangau'],
    'Mukah': ['Mukah', 'Dalat', 'Matu', 'Daro'],
    'Bintulu': ['Bintulu', 'Tatau'],
    'Kapit': ['Kapit', 'Belaga', 'Song'],
    'Miri': ['Miri', 'Marudi'],
    'Limbang': ['Limbang', 'Lawas']
  };

  // --- Create a flat list of all districts for "Select All" ---
  const allDistricts = useMemo(() => 
    Object.values(divisionGroupings).flat(), 
    [] // divisionGroupings is constant, so this only runs once
  );
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const regionsQuery = selectedRegions.join(',');
        
        // Handle case with no selected regions
        if (!regionsQuery) {
          setData(null);
          setLoading(false);
          return;
        }

        // Use a fixed wide date range for forest/mangrove to get all data points
        // The backend will correctly select [2010, 2015, 2020]
        const start_date = compareType === 'fire' ? startDate.format('YYYY-MM-DD') : '2010-01-01';
        const end_date = compareType === 'fire' ? endDate.format('YYYY-MM-DD') : '2020-12-31';
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        
        const response = await fetch(`${API_BASE_URL}/analysis/regional-comparison?type=${compareType}&regions=${regionsQuery}&start_date=${start_date}&end_date=${end_date}`);
        
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Failed to fetch regional comparison data');
        }
        
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (startDate && endDate && startDate.isSameOrBefore(endDate)) {
        fetchData();
    } else if (startDate && endDate) {
        setError("Start date must be on or before end date.");
    }

  }, [compareType, selectedRegions, startDate, endDate]);
  
  
  // --- Handler for the multi-select dropdown ---
  const handleRegionSelectChange = (event) => {
    const {
      target: { value },
    } = event;

    // Handle "Select All"
    if (value.includes('all')) {
      setSelectedRegions(allDistricts);
      return;
    }
    
    // Handle "Deselect All"
    if (value.includes('none')) {
      setSelectedRegions([]);
      return;
    }

    // Handle regular selection
    setSelectedRegions(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const getDataLabel = () => {
    if (compareType === 'fire') return 'Fire Alerts';
    if (compareType === 'mangrove') return 'Mangrove Coverage (km²)';
    if (compareType === 'forest') return 'Forest Coverage (km²)';
    return 'Data';
  };
  
  const getYearRangeTitle = () => {
    // Show specific years for forest/mangrove, date range for fire
    if (compareType === 'forest' || compareType === 'mangrove') {
      return '(2010, 2015, 2020)';
    }
    return `(${startDate.format('YYYY')} - ${endDate.format('YYYY')})`;
  };

  const handleExpandChart = (chartType) => setExpandedChart(chartType);
  const handleCloseDialog = () => setExpandedChart(null);
  
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: getDataLabel()
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: 'Percentage Change (%)'
        }
      }
    }
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid item xs={12} md={4} lg={3}>
          <Typography variant="h6">Regional Comparison Analysis</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2} lg={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Compare</InputLabel>
            <Select
              value={compareType}
              label="Compare"
              onChange={(e) => setCompareType(e.target.value)}
            >
              <MenuItem value="fire">Fire Alerts</MenuItem>
              <MenuItem value="forest">Forest Coverage</MenuItem>
              <MenuItem value="mangrove">Mangrove Coverage</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        {/* Conditionally render date pickers only for 'fire' analysis */}
        {compareType === 'fire' && (
          <>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  sx={{ width: '100%' }}
                  views={['year', 'month']}
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  sx={{ width: '100%' }}
                  views={['year', 'month']}
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
          </>
        )}
        
        <Grid item xs={12} sm={6} md={compareType === 'fire' ? 12 : 8} lg={compareType === 'fire' ? 3 : 6}>
          <FormControl size="small" fullWidth>
            <InputLabel id="district-multi-select-label">Select Districts</InputLabel>
            <Select
              labelId="district-multi-select-label"
              multiple
              value={selectedRegions}
              onChange={handleRegionSelectChange}
              input={<OutlinedInput label="Select Districts" />}
              renderValue={(selected) => `${selected.length} selected`}
              MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
            >
              {/* --- Select All / Deselect All options --- */}
              <MenuItem value="all">
                <Checkbox 
                  checked={allDistricts.length > 0 && selectedRegions.length === allDistricts.length}
                  indeterminate={selectedRegions.length > 0 && selectedRegions.length < allDistricts.length}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={selectedRegions.length === 0} />
                <ListItemText primary="Deselect All" />
              </MenuItem>

              {/* --- Grouped district options --- */}
              {Object.entries(divisionGroupings).map(([division, districts]) => ([
                <ListSubheader key={`${division}-header`}>{division} Division</ListSubheader>,
                ...districts.map((district) => (
                  <MenuItem key={district} value={district} sx={{ pl: 4 }}>
                    <Checkbox checked={selectedRegions.includes(district)} />
                    <ListItemText primary={district} />
                  </MenuItem>
                ))
              ]))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <LoadingState loading={loading} error={error}>
        {!data ? (
          <Alert severity="info">Please select at least one district to see results.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                cursor: 'pointer',
                flex: { xs: 'none', lg: '1 1 60%' },
                minWidth: 0
              }}
              onClick={() => handleExpandChart('annual')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Annual {getDataLabel()} by District {getYearRangeTitle()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  🔍 Click to enlarge
                </Typography>
              </Box>
              <Box sx={{ width: '100%', height: { xs: 350, md: 500 } }}>
                <Line data={data.annualTrends} options={lineChartOptions} />
              </Box>
            </Paper>

            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                flex: { xs: 'none', lg: '1 1 40%' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    % Change {getYearRangeTitle()}
                  </Typography>
                  <Tooltip 
                    title={
                      compareType === 'fire' 
                        ? "Shows the percentage change in fire alerts from the start year to the end year. Positive values indicate an increase in fire activity, while negative values indicate a decrease."
                        : compareType === 'mangrove'
                        ? "Shows the percentage change in mangrove coverage from the start year to the end year. Positive values indicate growth, while negative values indicate loss."
                        : "Shows the percentage change in forest coverage from the start year to the end year. Positive values indicate growth, while negative values indicate loss."
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: 'info.main', 
                        cursor: 'help',
                        '&:hover': {
                          color: 'info.dark'
                        }
                      }} 
                    />
                  </Tooltip>
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleExpandChart('change')}
                >
                  🔍 Click to enlarge
                </Typography>
              </Box>
              <Box 
                sx={{ width: '100%', height: { xs: 350, md: 500 }, flex: 1, cursor: 'pointer' }}
                onClick={() => handleExpandChart('change')}
              >
                <Bar data={data.percentageChange} options={barChartOptions} />
              </Box>
              <Alert severity="info" sx={{ mt: 1.5, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}>
                <Typography variant="caption">
                  {compareType === 'fire' 
                    ? "This chart shows the percentage change in fire alerts from start year to the end year."
                    : compareType === 'mangrove'
                    ? "This chart shows the percentage change in mangrove coverage from 2010 to 2020."
                    : "This chart shows the percentage change in forest coverage from 2010 to 2020."}
                  <br />
                  {compareType === 'fire' 
                    ? "Positive values indicate an increase in fire activity, while negative values indicate a decrease."
                    : compareType === 'mangrove'
                    ? "Positive values indicate growth, while negative values indicate loss."
                    : "Positive values indicate forest growth, while negative values indicate forest loss."}
                </Typography>
              </Alert>
            </Paper>
          </Box>
        )}
      </LoadingState>

      {/* Dialog for expanded charts */}
      <Dialog open={!!expandedChart} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {expandedChart === 'annual' && `Annual ${getDataLabel()} by District ${getYearRangeTitle()}`}
          {expandedChart === 'change' && `Percentage Change ${getYearRangeTitle()}`}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box height={{ xs: '60vh', md: 500 }}>
            {expandedChart === 'annual' && (
              <Line data={data?.annualTrends} options={lineChartOptions} />
            )}
            {expandedChart === 'change' && (
              <Bar data={data?.percentageChange} options={barChartOptions} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      
    </Box>
  );
};

export default RegionalComparisonAnalysis;